'use client'

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useLanguage } from '@/contexts/LanguageContext'
import { trustBadgesTranslations } from '@/translations/trustBadges'
import { Shield, ShieldPlus, BadgeCheck, Star, Clock, Sparkles, AlertTriangle, MapPin, X } from 'lucide-react'
import { useState, useEffect } from 'react'

interface Badge {
  icon: typeof Shield
  title: string
  description: string
  color: string
  link?: string
  external?: boolean
}

export default function TrustBadges() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const { language } = useLanguage()
  const t = trustBadgesTranslations[language]

  const badges: Badge[] = [
    {
      icon: Shield,
      title: t.badges.insurance.title,
      description: t.badges.insurance.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: ShieldPlus,
      title: t.badges.certified.title,
      description: t.badges.certified.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: BadgeCheck,
      title: t.badges.noHiddenCosts.title,
      description: t.badges.noHiddenCosts.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: Star,
      title: t.badges.experience.title,
      description: t.badges.experience.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: Clock,
      title: t.badges.reliable.title,
      description: t.badges.reliable.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: Sparkles,
      title: t.badges.quality.title,
      description: t.badges.quality.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: AlertTriangle,
      title: t.badges.healthSafety.title,
      description: t.badges.healthSafety.description,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      icon: MapPin,
      title: t.badges.localExpertise.title,
      description: t.badges.localExpertise.description,
      color: 'bg-gray-100 text-gray-700'
    },
  ]

  const importantBadgeTitles = [
    t.badges.insurance.title,
    t.badges.certified.title,
    t.badges.noHiddenCosts.title,
    t.badges.localExpertise.title
  ];

  const mobileBadges = badges.filter(badge =>
    [t.badges.insurance.title, t.badges.certified.title, t.badges.noHiddenCosts.title].includes(badge.title)
  );
  const tabletBadges = badges.filter(badge => importantBadgeTitles.includes(badge.title));

  const [showSheet, setShowSheet] = useState(false);

  // Prevent background scroll when sheet is open
  useEffect(() => {
    const html = document.documentElement;
    if (showSheet) {
      document.body.classList.add('overflow-hidden');
      html.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
      html.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
      html.classList.remove('overflow-hidden');
    };
  }, [showSheet]);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Mobile: show only the most important badges */}
        <div className="grid grid-cols-1 gap-8 sm:hidden">
          {mobileBadges.map((badge, index) => {
            const IconComponent = badge.icon;
            const BadgeContent = (
              <>
                <div className="w-16 h-16 bg-gray-700 rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {badge.title}
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {badge.description}
                </p>
              </>
            );

            return (
              <motion.div
                key={badge.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`text-center ${badge.link ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
                onClick={badge.link ? () => window.open(badge.link, badge.external ? '_blank' : '_self') : undefined}
              >
                {BadgeContent}
              </motion.div>
            );
          })}
        </div>
        {/* See all trust badges button (mobile only) */}
        <div className="flex justify-center mt-6 sm:hidden">
          <button
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-700 text-white font-semibold shadow hover:bg-emerald-800 transition-all text-base"
            onClick={() => setShowSheet(true)}
            aria-label="See all trust badges"
          >
            <BadgeCheck className="w-5 h-5" />
            See all trust badges
          </button>
        </div>
        {/* Dismissible bottom sheet for all badges (mobile only) */}
        {showSheet && (
           <div className="fixed inset-0 z-[9999] flex items-end justify-center sm:flex lg:hidden">
             {/* Overlay */}
             <div
               className="absolute inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
               onClick={() => setShowSheet(false)}
               aria-label="Close trust badges sheet"
             />
             {/* Sheet */}
             <div className="relative w-full max-w-full bg-white rounded-t-3xl shadow-lg p-6 pt-12 animate-slide-up max-h-[90vh] overflow-y-auto overflow-x-hidden hide-scrollbar z-[9999]">
               <button
                 className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-900 bg-white rounded-full p-1 shadow"
                 onClick={() => setShowSheet(false)}
                 aria-label="Close"
               >
                 <X className="w-6 h-6" />
               </button>
               <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">All Trust Badges</h3>
               <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 w-full max-w-full overflow-x-hidden">
                 {badges.map((badge) => (
                   <motion.div
                     key={badge.title}
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.5, delay: 0.1 }}
                     className={`text-center ${badge.link ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
                     onClick={badge.link ? () => window.open(badge.link, badge.external ? '_blank' : '_self') : undefined}
                   >
                     <div className="w-14 h-14 bg-gray-700 rounded-2xl shadow flex items-center justify-center mx-auto mb-3">
                       <badge.icon className="w-7 h-7 text-white" />
                     </div>
                     <h4 className="text-base font-semibold text-gray-900 mb-1">{badge.title}</h4>
                     <p className="text-xs text-gray-600 leading-relaxed">{badge.description}</p>
                   </motion.div>
                 ))}
               </div>
             </div>
           </div>
         )}
        {/* Tablet: show 4 most important badges in a 2-column grid, with 'See all' button */}
        <div className="hidden sm:grid lg:hidden gap-8 sm:grid-cols-2">
          {tabletBadges.map((badge, index) => {
            const IconComponent = badge.icon;
            const BadgeContent = (
              <>
                <div className="w-16 h-16 bg-gray-700 rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {badge.title}
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {badge.description}
                </p>
              </>
            );

            return (
              <motion.div
                key={badge.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`text-center ${badge.link ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
                onClick={badge.link ? () => window.open(badge.link, badge.external ? '_blank' : '_self') : undefined}
              >
                {BadgeContent}
              </motion.div>
            );
          })}
        </div>
        {/* See all trust badges button (tablet only) */}
        <div className="hidden sm:flex lg:hidden justify-center mt-6">
          <button
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-700 text-white font-semibold shadow hover:bg-emerald-800 transition-all text-base"
            onClick={() => setShowSheet(true)}
            aria-label="See all trust badges"
          >
            <BadgeCheck className="w-5 h-5" />
            See all trust badges
          </button>
        </div>
        {/* Tablet/Desktop: show all badges in a grid (lg:grid-cols-4) */}
        <div className="hidden lg:grid gap-8 lg:grid-cols-4">
          {badges.map((badge, index) => {
            const IconComponent = badge.icon;
            const BadgeContent = (
              <>
                <div className="w-16 h-16 bg-gray-700 rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {badge.title}
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {badge.description}
                </p>
              </>
            );

            return (
              <motion.div
                key={badge.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`text-center ${badge.link ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
                onClick={badge.link ? () => window.open(badge.link, badge.external ? '_blank' : '_self') : undefined}
              >
                {BadgeContent}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  )
} 